import type { BackendNotice, OperationMode } from "../../../../http/lock_calculator/types";
import { missingConnectorsInputs } from "./connectorsCompleteness";
import type { CalculationCoefficients, LockCalculatorFormValues } from "./form";
import { NOTICE_CATALOG, type NoticeCatalogEntry, type NoticeCode, type NoticeOrigin } from "./noticeCatalog";

export type NoticeSeverity = "info" | "warning" | "error";
export type WizardStepIndex = 0 | 1 | 2 | 3;

/** A message tied to the form fields that cause it. Never persisted: a notice exists only while its condition holds. */
export type CalculatorNotice = {
  id: string;
  code: string;
  severity: NoticeSeverity;
  /** "state" follows the current data, "cascade" is "X was changed because of Y", "session" depends on what happened since the page opened. */
  kind: "state" | "cascade" | "session";
  /** Form paths (react-hook-form) that trigger it; the first is where "go to field" lands. */
  fields: string[];
  /** Used when `fields` is empty. */
  step?: WizardStepIndex;
  /** The calculation button that resolves it (auto, semi or manual): "go to" lands on that button instead of a field. */
  action?: OperationMode;
  params: Record<string, unknown>;
};

/** Auto generation lives in the structure step; semi-automatic and manual ones in the connectors step. */
export const stepOfAction = (action: OperationMode): WizardStepIndex => (action === "generateAll" ? 2 : 3);

const KIND_BY_ORIGIN: Record<NoticeOrigin, CalculatorNotice["kind"]> = {
  backend: "state",
  form: "state",
  session: "session",
  cascade: "cascade",
};

/**
 * A notice the frontend builds, filled from the catalog: its kind, its fixed severity and the button
 * that resolves it. `severity` overrides it where the catalog leaves it to a runtime condition, and
 * `generationMode` is the button of the "generationMode" action.
 */
const catalogNotice = (
  code: NoticeCode,
  init: {
    id?: string;
    fields?: string[];
    params?: Record<string, unknown>;
    severity?: NoticeSeverity;
    generationMode?: OperationMode;
  },
): CalculatorNotice => {
  const entry: NoticeCatalogEntry = NOTICE_CATALOG[code];
  return {
    id: init.id ?? code,
    code,
    severity: init.severity ?? entry.severity ?? "info",
    kind: KIND_BY_ORIGIN[entry.origin],
    fields: init.fields ?? [],
    action: entry.action === "generationMode" ? init.generationMode : entry.action,
    params: init.params ?? {},
  };
};

const SEVERITY_RANK: Record<NoticeSeverity, number> = { error: 0, warning: 1, info: 2 };

/** Backend field names (update body) to form paths: the only place that knows both vocabularies. */
export const API_FIELD_TO_FORM_PATH: Record<string, string> = {
  standardId: "environment.standardId",
  serviceClassId: "environment.serviceClassId",
  loadDurationClassId: "environment.loadDurationClassId",
  continuousCtrl: "environment.continuousControl",
  "connectors[0].lockItemId": "connectorPairs.0.lockItemId",
  "connectors[1].lockItemId": "connectorPairs.1.lockItemId",
  headerFastenerItemId: "fasteners.headerItemId",
  joistFastenerItemId: "fasteners.joistItemId",
  inclinedFastenerItemId: "fasteners.inclinedItemId",
  lockstopItemId: "lockstopItemId",
  headerMaterialGradeId: "headerMember.materialGradeId",
  joistMaterialGradeId: "joistMember.materialGradeId",
  headerOrientation: "headerMember.orientation",
  headerB: "headerMember.width",
  headerH: "headerMember.height",
  joistB: "joistMember.width",
  joistH: "joistMember.height",
  alphaDeg: "joistMember.alphaDeg",
  lateralResistanceConfig: "lateralResistanceConfig",
  connectorTopDistance: "joistMember.connectorTopDistance",
  joistTopOffset: "joistMember.topOffsetFromHeader",
  joistTopAlignment: "joistMember.topAlignment",
  axialLoad: "loads.axial",
  verticalLoad: "loads.vertical",
  upliftLoad: "loads.uplift",
  lateralLoad: "loads.lateral",
  includeFv: "combinedVerification.includeVertical",
  includeFup: "combinedVerification.includeUplift",
  includeFax: "combinedVerification.includeAxial",
  includeFlat: "combinedVerification.includeLateral",
};

const FORM_PATH_TO_API_FIELD: Record<string, string> = Object.fromEntries(
  Object.entries(API_FIELD_TO_FORM_PATH).map(([field, path]) => [path, field]),
);

/** The backend name of a form path (the key the input labels are filed under); the input itself when it has none. */
export const apiFieldOfPath = (path: string): string => FORM_PATH_TO_API_FIELD[path] ?? path;

/** Wizard step that owns a form path (0 code/norm, 1 loads, 2 structure, 3 connectors). */
export const stepOfPath = (path: string): WizardStepIndex => {
  if (path.startsWith("environment.")) return 0;
  if (path.startsWith("loads.") || path.startsWith("combinedVerification.")) return 1;
  if (
    path.startsWith("headerMember.") ||
    path.startsWith("joistMember.") ||
    path === "lateralResistanceConfig" ||
    path === "upliftResistanceConfig"
  ) {
    return 2;
  }
  return 3;
};

export const noticeStep = (notice: CalculatorNotice): WizardStepIndex => {
  if (notice.action) return stepOfAction(notice.action);
  const first = notice.fields[0];
  return first !== undefined ? stepOfPath(first) : (notice.step ?? 3);
};

/** Errors first, then warnings, then info; stable inside a severity. */
export const sortNotices = (notices: CalculatorNotice[]): CalculatorNotice[] =>
  notices
    .map((notice, index) => ({ notice, index }))
    .sort((a, b) => SEVERITY_RANK[a.notice.severity] - SEVERITY_RANK[b.notice.severity] || a.index - b.index)
    .map(({ notice }) => notice);

export const fromBackendNotices = (notices: BackendNotice[] | undefined): CalculatorNotice[] =>
  (notices ?? []).map((notice) => {
    const params = notice.params ?? {};
    // Axis and member tell apart the notices that share a code.
    const discriminator = [params["axis"], params["member"]].find((value) => typeof value === "string") ?? "";
    return {
      id: `${notice.code}:${discriminator}`,
      code: notice.code,
      severity: notice.severity,
      kind: "state",
      fields: notice.fields.map((field) => API_FIELD_TO_FORM_PATH[field]).filter((path): path is string => !!path),
      params,
    };
  });

/** An axis notice whose every missing input the manual-solution notice already lists adds nothing to it. */
export const dropAxisNoticesCoveredBy = (notices: CalculatorNotice[], manual: CalculatorNotice[]): CalculatorNotice[] => {
  const covered = new Set(manual.flatMap((notice) => (Array.isArray(notice.params["missing"]) ? (notice.params["missing"] as string[]) : [])));
  if (covered.size === 0) return notices;
  return notices.filter((notice) => {
    const missing = notice.params["missing"];
    if (notice.code !== "axisInputsMissing" || !Array.isArray(missing) || missing.length === 0) return true;
    return !missing.every((key) => covered.has(API_FIELD_TO_FORM_PATH[String(key)] ?? ""));
  });
};

/** Reads a react-hook-form style path ("connectorPairs.0.lockItemId") from the form values. */
export const getPathValue = (values: unknown, path: string): unknown =>
  path.split(".").reduce<unknown>((node, key) => (node == null ? undefined : (node as Record<string, unknown>)[key]), values);

const sameValue = (a: unknown, b: unknown) => (a ?? null) === (b ?? null);

/** "x because of y": a field the rules changed. It stays while the cause keeps the value that triggered it and the effect keeps the applied one. */
export type CascadeRecord = {
  id: string;
  code: string;
  severity: NoticeSeverity;
  causes: { path: string; value: unknown }[];
  effect: { path: string; value: unknown };
  params: Record<string, unknown>;
};

export const isCascadeActive = (record: CascadeRecord, values: unknown): boolean =>
  sameValue(getPathValue(values, record.effect.path), record.effect.value) &&
  record.causes.every((cause) => sameValue(getPathValue(values, cause.path), cause.value));

export const cascadeToNotice = (record: CascadeRecord): CalculatorNotice => ({
  id: record.id,
  code: record.code,
  severity: record.severity,
  kind: "cascade",
  fields: [record.effect.path, ...record.causes.map((cause) => cause.path)],
  params: record.params,
});

/** The coefficients section of the environment step: one target for the notice that opens its fields. */
const COEFFICIENTS_SECTION = "environment.coefficients";

/** The three coefficient overrides, each read against the normative value. */
const COEFFICIENT_OVERRIDES = [
  { path: "environment.kmodOverride", key: "kmod", normative: "kmodDefault" },
  { path: "environment.gammaMConnectionOverride", key: "gammaM", normative: "gammaMDefault" },
  { path: "environment.gammaMAluminiumOverride", key: "gammaMAluminium", normative: "gammaMAluminiumDefault" },
] as const;

/** Shared with the coefficient fields' "restore" button. */
export const coefficientDiffers = (value: number | null | undefined, normative: number | null | undefined): boolean =>
  normative != null && (value ?? null) !== normative;

/** State notices that need only the form and the resolved coefficients (no backend rule). */
export const buildFormNotices = (
  values: LockCalculatorFormValues | undefined,
  coefficients: CalculationCoefficients,
): CalculatorNotice[] => {
  if (!values) return [];
  const notices: CalculatorNotice[] = [];
  if (values.environment.customCoefficients) {
    COEFFICIENT_OVERRIDES.forEach(({ path, key, normative }) => {
      const overrideKey = path.replace("environment.", "") as keyof LockCalculatorFormValues["environment"];
      if (coefficientDiffers(values.environment[overrideKey] as number | null, coefficients[normative])) {
        notices.push(
          catalogNotice("coefficientOverrideDiffers", {
            id: `coefficientOverrideDiffers:${key}`,
            fields: [path],
            params: { coefficient: key, value: values.environment[overrideKey], normative: coefficients[normative] },
          }),
        );
      }
    });
    // Switched on and nothing changed yet: say the fields are open; a changed value has its own warning.
    if (notices.length === 0) {
      notices.push(catalogNotice("customCoefficientsEnabled", { fields: [COEFFICIENTS_SECTION, ...COEFFICIENT_OVERRIDES.map(({ path }) => path)] }));
    }
  }
  const { connectorTopDistance, connectorTopDistanceDefault, topAlignment } = values.joistMember;
  if (
    values.headerMember.orientation === "column" &&
    topAlignment !== "offset" &&
    connectorTopDistance != null &&
    connectorTopDistance === connectorTopDistanceDefault
  ) {
    notices.push(
      catalogNotice("topDistanceEqualsDefault", {
        fields: ["joistMember.connectorTopDistance"],
        params: { value: connectorTopDistance },
      }),
    );
  }
  return notices;
};

/**
 * The required fields the manual solution still lacks; "go to" lands on the first of them, not on the
 * button. The button's own rule (isConnectorsStepComplete) owns the requirement. Silent until the
 * connectors configuration is started, a warning once a result exists (a field was deselected).
 */
export const buildManualGenerationNotices = (values: LockCalculatorFormValues | undefined, hasResult: boolean): CalculatorNotice[] => {
  if (!values) return [];
  const missing = missingConnectorsInputs(values);
  const started =
    hasResult ||
    !!values.connectorPairs?.[0]?.lockItemId ||
    !!values.fasteners?.headerItemId ||
    !!values.fasteners?.joistItemId ||
    !!values.fasteners?.inclinedItemId ||
    !!values.lockstopItemId;
  if (missing.length === 0 || !started) return [];
  return [
    catalogNotice("manualGenerationInputsMissing", {
      severity: hasResult ? "warning" : "info",
      fields: missing,
      params: { missing },
    }),
  ];
};

/** The input signatures in force when the final result and the drafts were last produced; null until the page has data. */
export type StalenessBaselines = {
  result: string | null;
  drafts: string | null;
  /** How the drafts were generated, so a stale list points at the same button (auto until a generation runs here). */
  draftsMode: OperationMode;
  /** The last generation produced no draft although locks were compatible. */
  lastGenerationEmpty: boolean;
  /** The inputs the shown PDF report was generated with; null while no report was generated here. */
  report: string | null;
};

/** Which generations cannot run because a required field is missing. */
export type BlockedGenerations = { manual: boolean; automatic: boolean };

/**
 * "Modified after the last calculation": the current inputs' signature differs from the one the result/drafts
 * were produced with. Once a generation is blocked by a missing field, its "recalculate" notice stays quiet:
 * the missing field comes first (it has its own notice, tied to the field). The report is stale once
 * its own signature (loads included) differs from the one it was generated with.
 */
export const buildSessionNotices = (
  signatures: { definition: string; full: string; report: string } | null,
  baselines: StalenessBaselines,
  hasResult: boolean,
  hasDrafts: boolean,
  blocked: BlockedGenerations = { manual: false, automatic: false },
): CalculatorNotice[] => {
  if (signatures == null) return [];
  const notices: CalculatorNotice[] = [];
  const generationMode = baselines.draftsMode;
  const resultStale = !blocked.manual && hasResult && baselines.result != null && baselines.result !== signatures.full;
  if (resultStale) {
    notices.push(catalogNotice("resultStale", {}));
  }
  // A stale result (or a blocked recalculation) comes first: the report is regenerated after, so it waits.
  if (!blocked.manual && !resultStale && baselines.report != null && baselines.report !== signatures.report) {
    notices.push(catalogNotice("reportStale", {}));
  }
  const draftsStale = baselines.drafts != null && baselines.drafts !== signatures.definition;
  if (!blocked.automatic && hasDrafts && draftsStale) {
    notices.push(catalogNotice("draftsStale", { generationMode }));
  }
  if (!blocked.automatic && baselines.lastGenerationEmpty && !draftsStale) {
    notices.push(catalogNotice("noDraftWithinLimits", { generationMode }));
  }
  return notices;
};

/**
 * The notices whose text shows under this field: the ones whose first field it is (a group name such as
 * "combinedVerification" stands for the fields under it). A notice names several fields, to trigger it and to
 * lead "go to", but says its text once: the same message is not repeated down the form.
 */
export const noticesShownAtField = (notices: CalculatorNotice[], path: string): CalculatorNotice[] =>
  notices.filter((notice) => {
    const home = notice.fields[0];
    return home !== undefined && (home === path || home.startsWith(`${path}.`));
  });
